import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IDENTITY_LIMITS, type IdentityDto, type IdentitySummaryDto, type ReferenceAngle } from '@gc/shared';
import { ApiError } from '../api/client.js';
import {
  actualizarRevision,
  anadirReferencia,
  aprobarRevision,
  archivarIdentidad,
  crearIdentidad,
  crearRevision,
  generarReferencias,
  listarIdentidades,
  obtenerIdentidad,
  quitarReferencia,
} from '../api/identity.js';
import { SelectorAsset } from '../features/biblioteca/SelectorAsset.js';

const ANGULOS: Record<ReferenceAngle, string> = {
  front: 'Frontal',
  three_quarter: 'Tres cuartos',
  profile: 'Perfil',
  full_body: 'Cuerpo entero',
  other: 'Otro',
};

/**
 * Biblioteca de identidades virtuales (fase 10). Cada identidad es ficticia y
 * adulta; sus revisiones aprobadas son inmutables y son lo que usan los
 * proyectos de creador virtual.
 */
export function Identidades(): React.JSX.Element {
  const [lista, setLista] = useState<IdentitySummaryDto[] | null>(null);
  const [abierta, setAbierta] = useState<IdentityDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [rasgos, setRasgos] = useState('');

  const recargar = useCallback(async (id?: string) => {
    try {
      setLista(await listarIdentidades());
      const objetivo = id ?? abierta?.id;
      if (objetivo) setAbierta(await obtenerIdentidad(objetivo));
      setError(null);
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudieron cargar las identidades.');
    }
  }, [abierta?.id]);

  // Carga inicial de la lista; lo abierto se recarga tras cada acción.
  useEffect(() => {
    void listarIdentidades()
      .then(setLista)
      .catch((causa: unknown) => setError(causa instanceof ApiError ? causa.message : 'No se pudieron cargar las identidades.'));
  }, []);

  // Mientras haya referencias generándose se refresca.
  const generando = abierta?.revisions.some((r) => r.status === 'DRAFT') ?? false;
  useEffect(() => {
    if (!generando || abierta === null) return;
    const intervalo = setInterval(() => void recargar(abierta.id), 5_000);
    return () => clearInterval(intervalo);
  }, [generando, abierta, recargar]);

  async function accion(fn: () => Promise<unknown>, id?: string): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await fn();
      await recargar(id);
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'La operación falló.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section>
      <h1>Identidades virtuales</h1>
      <p>
        <small>
          Personajes ficticios adultos reutilizables en proyectos de creador virtual. Cada revisión
          aprobada queda fija: los proyectos que la usan no cambian aunque crees otra. Sin personas
          reales, sin clonación de voz, sin entrenamiento de modelos.
        </small>
      </p>

      <h2>Nueva identidad</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void accion(async () => {
            const creada = await crearIdentidad({ name: nombre, description: descripcion, lockedTraits: rasgos.split('\n').map((l) => l.trim()).filter(Boolean) });
            setNombre(''); setDescripcion(''); setRasgos('');
            setAbierta(creada);
          });
        }}
      >
        <p>
          <label htmlFor="id-nombre">Nombre</label><br />
          <input id="id-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={IDENTITY_LIMITS.nameMaxLength} required style={{ maxWidth: '30rem', width: '100%' }} />
        </p>
        <p>
          <label htmlFor="id-descripcion">Descripción (en inglés, para los prompts)</label><br />
          <textarea id="id-descripcion" rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required style={{ maxWidth: '40rem', width: '100%' }} placeholder="Fictional adult woman in her 30s, short dark hair, warm smile" />
        </p>
        <p>
          <label htmlFor="id-rasgos">Rasgos invariantes (uno por línea, en inglés)</label><br />
          <textarea id="id-rasgos" rows={3} value={rasgos} onChange={(e) => setRasgos(e.target.value)} required style={{ maxWidth: '40rem', width: '100%' }} placeholder={'short dark hair\ngreen linen shirt'} />
        </p>
        <button type="submit" disabled={ocupado}>Crear identidad</button>
      </form>

      <h2>Mis identidades</h2>
      {lista === null && <p role="status">Cargando…</p>}
      {lista !== null && lista.length === 0 && <p>Todavía no hay identidades.</p>}
      {lista !== null && lista.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {lista.map((i) => (
            <li key={i.id} style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '0.5rem 0.75rem', opacity: i.archivedAt ? 0.6 : 1 }}>
              <strong>{i.name}</strong>
              <br />
              <small>
                {i.approvedRevision === null ? 'sin revisión aprobada' : `revisión ${i.approvedRevision} aprobada`} · última {i.latestRevision}
                {i.archivedAt ? ' · archivada' : ''}
              </small>
              <br />
              <button type="button" onClick={() => void accion(async () => undefined, i.id)}>Abrir</button>
            </li>
          ))}
        </ul>
      )}

      {abierta !== null && (
        <article aria-labelledby="titulo-identidad" style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', marginTop: '1rem' }}>
          <h2 id="titulo-identidad" style={{ marginTop: 0 }}>{abierta.name}</h2>
          <p>
            <small>
              Proyecto de referencias: {abierta.referenceProjectId ? <Link to={`/proyectos/${abierta.referenceProjectId}`}>abrir</Link> : '—'}
              {abierta.archivedAt === null && (
                <>
                  {' '}·{' '}
                  <button type="button" disabled={ocupado} onClick={() => { if (window.confirm('¿Archivar la identidad? Los proyectos que la usan no cambian.')) void accion(() => archivarIdentidad(abierta.id), abierta.id); }}>Archivar</button>
                </>
              )}
              {' '}·{' '}
              <button type="button" disabled={ocupado || abierta.archivedAt !== null} onClick={() => void accion(() => crearRevision(abierta.id, {}), abierta.id)}>Nueva revisión</button>
            </small>
          </p>
          {abierta.revisions.map((rev) => (
            <Revision key={rev.id} identidad={abierta} rev={rev} ocupado={ocupado} onAccion={(fn) => accion(fn, abierta.id)} />
          ))}
        </article>
      )}

      {error !== null && <p role="alert">{error}</p>}
    </section>
  );
}

function Revision({ identidad, rev, ocupado, onAccion }: {
  identidad: IdentityDto; rev: IdentityDto['revisions'][number]; ocupado: boolean; onAccion(fn: () => Promise<unknown>): Promise<void>;
}): React.JSX.Element {
  const editable = rev.status === 'DRAFT' && !rev.inUse;
  const [descripcion, setDescripcion] = useState(rev.description);
  const [rasgos, setRasgos] = useState(rev.lockedTraits.join('\n'));
  const [derechos, setDerechos] = useState('');
  const [angulo, setAngulo] = useState<ReferenceAngle>('front');
  const [angulosGenerar, setAngulosGenerar] = useState<ReferenceAngle[]>(['front', 'three_quarter', 'profile']);
  useEffect(() => { setDescripcion(rev.description); setRasgos(rev.lockedTraits.join('\n')); }, [rev.description, rev.lockedTraits]);

  return (
    <details open={rev === identidad.revisions[0]} style={{ border: '1px solid var(--borde-suave)', borderRadius: '6px', padding: '0.75rem 1rem', margin: '0.75rem 0' }}>
      <summary>
        <strong>Revisión {rev.number}</strong> — {rev.status === 'APPROVED' ? `aprobada${rev.inUse ? ', en uso' : ''}` : rev.inUse ? 'borrador en uso' : 'borrador'} · {rev.references.length} referencia(s)
      </summary>

      <p>
        <label>Descripción<br />
          <textarea rows={2} value={descripcion} disabled={!editable || ocupado} onChange={(e) => setDescripcion(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
        </label>
      </p>
      <p>
        <label>Rasgos invariantes<br />
          <textarea rows={2} value={rasgos} disabled={!editable || ocupado} onChange={(e) => setRasgos(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
        </label>
      </p>
      {editable && (
        <p>
          <button type="button" disabled={ocupado} onClick={() => void onAccion(() => actualizarRevision(identidad.id, rev.id, { description: descripcion, lockedTraits: rasgos.split('\n').map((l) => l.trim()).filter(Boolean) }))}>
            Guardar rasgos
          </button>
        </p>
      )}

      <h3>Referencias</h3>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {rev.references.map((ref) => (
          <figure key={ref.id} style={{ margin: 0, width: '9rem' }}>
            <img src={ref.contentUrl} alt={`${identidad.name} — ${ref.angle ?? 'referencia'}`} style={{ width: '100%', height: '9rem', objectFit: 'cover', background: 'var(--fondo-hundido)' }} />
            <figcaption>
              <small>
                {ANGULOS[ref.angle as ReferenceAngle] ?? ref.angle ?? '—'} · {ref.source === 'GENERATED' ? 'generada' : 'subida'}
                <br />
                <span title={ref.rightsDeclaration}>derechos: {ref.rightsDeclaration.slice(0, 40)}{ref.rightsDeclaration.length > 40 ? '…' : ''}</span>
                {editable && (
                  <>
                    <br />
                    <button type="button" disabled={ocupado} onClick={() => void onAccion(() => quitarReferencia(identidad.id, rev.id, ref.id))}>Quitar</button>
                  </>
                )}
              </small>
            </figcaption>
          </figure>
        ))}
        {rev.references.length === 0 && <p><small>Sin referencias. Sube una imagen propia o genera las imágenes base.</small></p>}
      </div>

      {editable && identidad.referenceProjectId !== null && (
        <>
          <h4>Añadir una imagen propia</h4>
          <p>
            <label>Ángulo{' '}
              <select value={angulo} onChange={(e) => setAngulo(e.target.value as ReferenceAngle)}>
                {Object.entries(ANGULOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </p>
          <p>
            <label htmlFor={`derechos-${rev.id}`}>Declaración de derechos (obligatoria)</label><br />
            <input id={`derechos-${rev.id}`} value={derechos} onChange={(e) => setDerechos(e.target.value)} placeholder="Imagen propia / licencia…" style={{ width: '100%', maxWidth: '40rem' }} />
          </p>
          <SelectorAsset
            projectId={identidad.referenceProjectId}
            usage="LIBRARY"
            tiposPermitidos={['IMAGE']}
            seleccionado={null}
            deshabilitado={ocupado || derechos.trim() === ''}
            etiquetaSubida="Subir imagen de referencia"
            onSeleccionar={(assetId) => {
              if (assetId !== null) void onAccion(() => anadirReferencia(identidad.id, rev.id, { assetId, angle: angulo, rightsDeclaration: derechos }));
            }}
          />
          {derechos.trim() === '' && <p><small>Escribe la declaración de derechos antes de elegir o subir la imagen.</small></p>}

          <h4>Generar imágenes base con IA</h4>
          <p>
            <small>Usa el proveedor de imagen con las referencias actuales para mantener la identidad. Cada imagen cuesta según la tarifa publicada y se reserva del presupuesto del proyecto de referencias.</small>
          </p>
          <p>
            {(Object.keys(ANGULOS) as ReferenceAngle[]).filter((a) => a !== 'other').map((a) => (
              <label key={a} style={{ marginRight: '1rem' }}>
                <input type="checkbox" checked={angulosGenerar.includes(a)} onChange={(e) => setAngulosGenerar(e.target.checked ? [...angulosGenerar, a] : angulosGenerar.filter((x) => x !== a))} /> {ANGULOS[a]}
              </label>
            ))}
          </p>
          <p>
            <button type="button" disabled={ocupado || angulosGenerar.length === 0} onClick={() => void onAccion(() => generarReferencias(identidad.id, rev.id, { angles: angulosGenerar, quality: 'medium' }))}>
              Generar {angulosGenerar.length} imagen(es)
            </button>
          </p>
        </>
      )}

      {editable && (
        <p>
          <button type="button" disabled={ocupado || rev.references.length === 0} onClick={() => { if (window.confirm('Aprobar fija esta revisión: no podrá cambiarse. ¿Continuar?')) void onAccion(() => aprobarRevision(identidad.id, rev.id)); }}>
            Aprobar revisión {rev.number}
          </button>{' '}
          <small>Después solo podrás crear una revisión nueva.</small>
        </p>
      )}
    </details>
  );
}
